import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loader from "../../components/ui/Loader";
import EmptyState from "../../components/ui/EmptyState";
import { useApi } from "../../services/useApi";
import { useAuth } from "../auth/useAuth";
import {
  getTeams,
  removeTeamMember,
  updateTeam,
  updateTeamMember,
} from "./teamService";

const TeamEditPage = ({ teamIdOverride, onClose, onSaved }) => {
  const { teamId: teamIdParam } = useParams();
  const api = useApi();
  const { token, user } = useAuth();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [openMemberId, setOpenMemberId] = useState(null);
  const [draggingMemberId, setDraggingMemberId] = useState(null);
  const [memberLoading, setMemberLoading] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [error, setError] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(null);

  const getMemberKey = (member) => member?.publicId || member?.userPublicId;
  const getMemberApiId = (member) => member?.publicId;
  const currentUserId = user?.publicId;
  const isCurrentUserAdmin = Boolean(
    (team?.members || []).find((m) => m.userPublicId === currentUserId)?.isAdmin,
  );
  const setMemberBusy = (id, value) => {
    setMemberLoading((prev) => ({ ...prev, [id]: value }));
  };

  const refreshMembers = async () => {
    const resolvedTeamId = teamIdOverride || teamIdParam;
    if (!token || !resolvedTeamId) return;
    try {
      const data = await getTeams(api);
      const found = (data || []).find((t) => t.publicId === resolvedTeamId);
      if (found?.members) {
        setTeam((prev) => (prev ? { ...prev, members: found.members } : prev));
      }
    } catch {
      // ignore refresh errors
    }
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const resolvedTeamId = teamIdOverride || teamIdParam;
      if (!token || !resolvedTeamId) {
        setLoading(false);
        return;
      }
      try {
        const data = await getTeams(api);
        const found = (data || []).find(
          (t) => t.publicId === (teamIdOverride || teamIdParam),
        );
        if (!mounted) return;
        setTeam(found || null);
        setName(found?.name || "");
        setPhotoPreview(found?.pictureUrl || "");
        setPhotoFile(null);
        setError("");
      } catch {
        if (!mounted) return;
        setTeam(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [api, token, teamIdParam, teamIdOverride]);

  const grouped = useMemo(() => {
    const members = team?.members || [];
    const admins = members.filter((m) => m.isAdmin);
    const anglers = members.filter(
      (m) => m.memberType === "SPORTSMAN" && !m.isAdmin,
    );
    const viewers = members.filter(
      (m) => m.memberType === "VIEWER" && !m.isAdmin,
    );
    return { admins, anglers, viewers };
  }, [team]);

  const isDirty = useMemo(() => {
    if (!team) return false;
    return name.trim() !== (team.name || "").trim() || !!photoFile;
  }, [name, photoFile, team]);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleMemberMenu = (e, id) => {
    e.stopPropagation();
    if (!isCurrentUserAdmin) return;
    setOpenMemberId((current) => (current === id ? null : id));
  };

  const handleRemoveMember = (e, member) => {
    e.stopPropagation();
    if (!isCurrentUserAdmin) return;
    setConfirmRemove(member);
    setOpenMemberId(null);
  };

  const handleSave = async () => {
    if (!team) return;
    setSaving(true);
    setError("");
    try {
      let updatedTeam = team;
      if (name.trim() !== (team.name || "").trim() || photoFile) {
        const formData = new FormData();
        const payload = { name: name.trim() };
        formData.append(
          "request",
          new Blob([JSON.stringify(payload)], { type: "application/json" }),
        );
        if (photoFile) {
          formData.append("picture", photoFile);
        }
        const updated = await updateTeam(api, team.publicId, formData);
        updatedTeam = updated
          ? { ...team, ...updated }
          : {
              ...team,
              name: name.trim(),
              pictureUrl: photoPreview,
            };
      }

      setTeam(updatedTeam);
      setName(updatedTeam?.name || name);
      setPhotoPreview(updatedTeam?.pictureUrl || photoPreview);
      setPhotoFile(null);
      setConfirmSaveOpen(false);
      if (onSaved) {
        onSaved(updatedTeam);
      }
    } catch (err) {
      console.error("Save team failed", err);
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not save team changes. Please try again.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const updateMember = async (member, payload) => {
    if (!member) return;
    const memberId = getMemberKey(member);
    if (!memberId) return;
    const prevMembers = team?.members || [];
    setMemberBusy(memberId, true);
    setTeam((current) => {
      if (!current) return current;
      return {
        ...current,
        members: (current.members || []).map((m) =>
          getMemberKey(m) === memberId ? { ...m, ...payload } : m,
        ),
      };
    });
    try {
      const apiId = getMemberApiId(member) || memberId;
      try {
        await updateTeamMember(api, apiId, payload);
      } catch (err) {
        if (member?.userPublicId && member.userPublicId !== apiId) {
          await updateTeamMember(api, member.userPublicId, payload);
        } else {
          throw err;
        }
      }
      await refreshMembers();
    } catch (err) {
      setTeam((current) => (current ? { ...current, members: prevMembers } : current));
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not update member. Please try again.";
      setError(message);
    } finally {
      setMemberBusy(memberId, false);
    }
  };

  const confirmRemoveMember = async () => {
    if (!confirmRemove) return;
    const member = confirmRemove;
    const memberId = getMemberKey(member);
    if (!memberId) return;
    setMemberBusy(memberId, true);
    setError("");
    try {
      const apiId = getMemberApiId(member) || memberId;
      try {
        await removeTeamMember(api, apiId);
      } catch (err) {
        if (member?.userPublicId && member.userPublicId !== apiId) {
          await removeTeamMember(api, member.userPublicId);
        } else {
          throw err;
        }
      }
      setTeam((current) => {
        if (!current) return current;
        return {
          ...current,
          members: (current.members || []).filter(
            (m) => getMemberKey(m) !== memberId,
          ),
        };
      });
      await refreshMembers();
      setConfirmRemove(null);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not remove member. Please try again.";
      setError(message);
    } finally {
      setMemberBusy(memberId, false);
    }
  };

  const handleMemberDrop = (e, target) => {
    e.preventDefault();
    if (!isCurrentUserAdmin || !draggingMemberId || !team?.members) return;
    const member = team.members.find(
      (m) => getMemberKey(m) === draggingMemberId,
    );
    if (!member) return;
    if (member.isAdmin) return;
    const isAdmin = member.isAdmin;
    const memberType = target === "VIEWER" ? "VIEWER" : "SPORTSMAN";
    if (
      member.isAdmin === isAdmin &&
      member.memberType === memberType
    ) {
      return;
    }
    updateMember(member, { memberType, isAdmin });
    setDraggingMemberId(null);
  };

  const handleDragStart = (e, memberId) => {
    if (!isCurrentUserAdmin) return;
    setDraggingMemberId(memberId);
    if (e?.dataTransfer) {
      e.dataTransfer.setData("text/plain", memberId);
    }
  };

  const handleMakeAdmin = (e, member) => {
    e.stopPropagation();
    if (!isCurrentUserAdmin) return;
    const memberId = getMemberKey(member);
    if (!memberId) return;
    updateMember(member, {
      memberType: member.memberType || "SPORTSMAN",
      isAdmin: true,
    });
    setOpenMemberId(null);
  };

  const handleRemoveAdmin = (e, member) => {
    e.stopPropagation();
    if (!isCurrentUserAdmin) return;
    const memberId = getMemberKey(member);
    if (!memberId) return;
    updateMember(member, {
      memberType: member.memberType,
      isAdmin: false,
    });
    setOpenMemberId(null);
  };

  const allowDrop = (e) => {
    if (!isCurrentUserAdmin) return;
    e.preventDefault();
  };

  return (
    <div className="team-edit">
      <div className="team-edit-header">
        <h2>Edit Team</h2>
        <div className="team-edit-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={!isDirty || saving}
            onClick={() => setConfirmSaveOpen(true)}
          >
            {saving ? "Saving..." : "Save"}
          </button>
          {onClose && (
            <button
              type="button"
              className="title-modal-close"
              onClick={onClose}
            >
              x
            </button>
          )}
        </div>
      </div>
      {error && <div className="team-edit-error">{error}</div>}
      {loading && <Loader />}
      {!loading && !team && (
        <EmptyState title="Team not found" message="No team data available." />
      )}
      {!loading && team && (
        <div className="team-edit-layout">
          <div className="card team-edit-card">
            <div className="team-edit-photo">
              <img
                src={photoPreview || "/icons/Logo_PNG.png"}
                alt={team.name || "Team"}
              />
              <label className="team-edit-upload">
                Change photo
                <input type="file" accept="image/*" onChange={handlePhotoChange} />
              </label>
              {photoFile && (
                <div className="team-edit-photo-note">New photo selected</div>
              )}
            </div>
            <div className="team-edit-fields">
              <label>
                Team name
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label>
                Invitation code
                <input className="input" value={team.inviteCode || ""} readOnly />
              </label>
            </div>
          </div>

          <div className="card team-edit-members">
            <div className="member-group">
              <div className="member-group-header">
                <h3>Admins</h3>
              </div>
              <div className="member-group-list">
                {grouped.admins.map((m) => {
                  const memberId = getMemberKey(m);
                  const isBusy = memberLoading[memberId];
                  return (
                    <div key={memberId} className="member-row compact">
                      <div className="member-main">
                        <img
                          src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                          alt={m.name || m.username || "Admin"}
                        />
                        <div className="member-info">
                          <div className="member-name">{m.name || m.username}</div>
                          <div className="member-sub">
                            Admin · {m.memberType === "VIEWER" ? "Viewer" : "Angler"}
                          </div>
                        </div>
                      </div>
                      {isCurrentUserAdmin && (
                        <button
                          type="button"
                          className="member-menu"
                          onClick={(e) => handleMemberMenu(e, memberId)}
                          disabled={isBusy}
                        >
                          ...
                        </button>
                      )}
                      {isCurrentUserAdmin && openMemberId === memberId && (
                        <div className="member-menu-popover">
                          <button
                            type="button"
                            onClick={(e) => handleRemoveAdmin(e, m)}
                            disabled={isBusy}
                          >
                            Remove admin
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleRemoveMember(e, m)}
                            disabled={isBusy}
                          >
                            Remove member
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {grouped.admins.length === 0 && (
                  <div className="member-group-hint">No admins yet.</div>
                )}
              </div>
            </div>

            <div className="member-group"
              onDragOver={allowDrop}
              onDrop={(e) => handleMemberDrop(e, "ANGLER")}
            >
            <div className="member-group-header">
                <h3>Anglers</h3>
                {isCurrentUserAdmin && (
                  <span className="member-group-hint">
                    Drop here to make angler
                  </span>
                )}
              </div>
              <div className="member-group-list">
                {grouped.anglers.map((m) => {
                  const memberId = getMemberKey(m);
                  const isBusy = memberLoading[memberId];
                  return (
                    <div
                      key={memberId}
                      className="member-row compact"
                      draggable={isCurrentUserAdmin && !isBusy}
                      onDragStart={(e) => handleDragStart(e, memberId)}
                    >
                      <div className="member-main">
                        <img
                          src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                          alt={m.name || m.username || "Angler"}
                        />
                        <div className="member-info">
                          <div className="member-name">{m.name || m.username}</div>
                          <div className="member-sub">Angler</div>
                        </div>
                      </div>
                      {isCurrentUserAdmin && (
                        <button
                          type="button"
                          className="member-menu"
                          onClick={(e) => handleMemberMenu(e, memberId)}
                          disabled={isBusy}
                        >
                          ...
                        </button>
                      )}
                      {isCurrentUserAdmin && openMemberId === memberId && (
                        <div className="member-menu-popover">
                          <button
                            type="button"
                            onClick={(e) => handleMakeAdmin(e, m)}
                            disabled={isBusy}
                          >
                            Make admin
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleRemoveMember(e, m)}
                            disabled={isBusy}
                          >
                            Remove member
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {grouped.anglers.length === 0 && (
                  <div className="member-group-hint">No anglers yet.</div>
                )}
              </div>
            </div>

            <div
              className="member-group"
              onDragOver={allowDrop}
              onDrop={(e) => handleMemberDrop(e, "VIEWER")}
            >
              <div className="member-group-header">
                <h3>Viewers</h3>
                {isCurrentUserAdmin && (
                  <span className="member-group-hint">
                    Drop here to make viewer
                  </span>
                )}
              </div>
              <div className="member-group-list">
                {grouped.viewers.map((m) => {
                  const memberId = getMemberKey(m);
                  const isBusy = memberLoading[memberId];
                  return (
                    <div
                      key={memberId}
                      className="member-row compact"
                      draggable={isCurrentUserAdmin && !isBusy}
                      onDragStart={(e) => handleDragStart(e, memberId)}
                    >
                      <div className="member-main">
                        <img
                          src={m.profilePictureUrl || "/icons/Logo_PNG.png"}
                          alt={m.name || m.username || "Viewer"}
                        />
                        <div className="member-info">
                          <div className="member-name">{m.name || m.username}</div>
                          <div className="member-sub">Viewer</div>
                        </div>
                      </div>
                      {isCurrentUserAdmin && (
                        <button
                          type="button"
                          className="member-menu"
                          onClick={(e) => handleMemberMenu(e, memberId)}
                          disabled={isBusy}
                        >
                          ...
                        </button>
                      )}
                      {isCurrentUserAdmin && openMemberId === memberId && (
                        <div className="member-menu-popover">
                          <button
                            type="button"
                            onClick={(e) => handleMakeAdmin(e, m)}
                            disabled={isBusy}
                          >
                            Make admin
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleRemoveMember(e, m)}
                            disabled={isBusy}
                          >
                            Remove member
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {grouped.viewers.length === 0 && (
                  <div className="member-group-hint">No viewers yet.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {confirmSaveOpen && (
        <div
          className="title-modal-backdrop"
        >
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Confirm Save</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setConfirmSaveOpen(false)}
              >
                x
              </button>
            </div>
            <div className="title-modal-body">
              <div>Are you sure you want to edit this team?</div>
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmSaveOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Yes, Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmRemove && (
        <div className="title-modal-backdrop">
          <div className="title-modal" onClick={(e) => e.stopPropagation()}>
            <div className="title-modal-header">
              <div className="title-modal-title">Remove member?</div>
              <button
                type="button"
                className="title-modal-close"
                onClick={() => setConfirmRemove(null)}
                disabled={memberLoading[getMemberKey(confirmRemove)]}
              >
                x
              </button>
            </div>
            <div className="title-modal-body">
              <div>
                Remove {confirmRemove?.name || confirmRemove?.username || "member"} from
                this team?
              </div>
            </div>
            <div className="title-modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setConfirmRemove(null)}
                disabled={memberLoading[getMemberKey(confirmRemove)]}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmRemoveMember}
                disabled={memberLoading[getMemberKey(confirmRemove)]}
              >
                {memberLoading[getMemberKey(confirmRemove)] ? "Removing..." : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamEditPage;

